import { zodResolver } from '@hookform/resolvers/zod';
import { ChannelType, useCreateChannelMutation } from 'api/ChannelsAPI';
import { WhatsApp } from 'constants/messageMediums';
import {
  CHANNEL_FORM_FIELDS_TEXT,
  CHANNEL_NAME,
  CHANNEL_TYPE,
  CHANNEL_TYPE_OPTIONS,
  WHATS_APP_CHANNEL_TYPE_OPTIONS,
} from 'modules/channels/constants';
import { ChannelFormState } from 'modules/channels/types';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ApiError } from 'services/api';
import { InfoBanner } from 'ui/banner';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { RadioList } from 'ui/radioList';
import { getMediumByKey } from 'utils/messageMediums';
import * as z from 'zod';

export const CREATE_CHANNEL_TEXT = {
  BANNER_TEXT: `Type can't be updated once subscription channel is created.`,
  BUTTON_LABEL: 'Create channel',
  ERROR_MESSAGE: 'Unable to create subscription channel.',
  FORM_ID: 'create-channel-form',
  MODAL_ID: 'create-channel-modal',
  SUCCESS_MESSAGE: 'Success! Created:',
};

type ModalProps = {
  onClose: () => void;
  messageMedium: string;
  refetchChannels: () => void;
};

const schema = z.object({
  [CHANNEL_NAME]: z.string().trim().min(1, { message: 'Enter a  name' }),
  [CHANNEL_TYPE]: z.nativeEnum(ChannelType),
});

export const CreateChannelModal = ({ messageMedium, onClose, refetchChannels }: ModalProps) => {
  const medium = getMediumByKey(messageMedium);
  const dispatch = useDispatch();

  const {
    control,
    getValues,
    handleSubmit,
    formState: { isSubmitted, isValid },
  } = useForm<ChannelFormState>({
    defaultValues: {
      channelName: '',
      channelType: ChannelType.Marketing,
    },
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });

  const onError = (e: unknown) => {
    const { message } = e as ApiError;
    const errorText = `${CREATE_CHANNEL_TEXT.ERROR_MESSAGE} ${message}.`;
    onClose();
    dispatch(addNegativeToastWithErrorId(errorText, 'CreateChannelModal', e));
  };

  const onSuccess = () => {
    const channelName = getValues('channelName');
    onClose();
    refetchChannels();
    dispatch(
      addPositiveToast(
        <>
          {CREATE_CHANNEL_TEXT.SUCCESS_MESSAGE} <strong>{channelName}</strong>
        </>,
      ),
    );
  };

  const { isLoading, mutate } = useCreateChannelMutation({ onSuccess, onError });

  const onSubmit: SubmitHandler<Partial<ChannelFormState>> = (values, event) => {
    event?.preventDefault();
    // if form or message type isn't valid, abort
    if (!isValid || !medium?.messageType) return null;

    const { channelType, channelName } = values;
    const name = channelName?.trim();
    const payload = {
      channelType,
      messageMedium: medium?.messageType,
      name,
    };

    mutate(payload);
  };

  return (
    <Modal
      id={CREATE_CHANNEL_TEXT.MODAL_ID}
      isModalOpen={true}
      onClose={onClose}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>New {medium?.adjective} channel</ModalHeader>
      <ModalBody>
        <form id={CREATE_CHANNEL_TEXT.FORM_ID} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name={CHANNEL_NAME}
            render={({ field: { onBlur, onChange, value, name }, fieldState: { error } }) => (
              <FormField
                labelText={CHANNEL_FORM_FIELDS_TEXT[CHANNEL_NAME].LABEL}
                hasError={Boolean(error?.message)}
                errorText={`${error?.message}`}
              >
                <InputText
                  autoFocus
                  data-test="channel-name-input"
                  id={CHANNEL_FORM_FIELDS_TEXT[CHANNEL_NAME].ID}
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={`${value}`}
                />
              </FormField>
            )}
          />
          <Controller
            control={control}
            name={CHANNEL_TYPE}
            render={({ field: { onChange, value, name }, fieldState: { error } }) => (
              <FormField
                labelText={CHANNEL_FORM_FIELDS_TEXT[CHANNEL_TYPE].LABEL}
                hasError={Boolean(error?.message)}
                errorText={error?.message}
              >
                <RadioList
                  name={name}
                  onSelect={onChange}
                  options={
                    medium?.messageType !== WhatsApp
                      ? CHANNEL_TYPE_OPTIONS
                      : WHATS_APP_CHANNEL_TYPE_OPTIONS
                  }
                  value={`${value}`}
                />
              </FormField>
            )}
          />
        </form>
        <InfoBanner>{CREATE_CHANNEL_TEXT.BANNER_TEXT}</InfoBanner>
      </ModalBody>
      <ModalFooter onClose={onClose}>
        <PrimaryButton
          data-test="create-channel-btn"
          form={CREATE_CHANNEL_TEXT.FORM_ID}
          isDisabled={!isValid && isSubmitted}
          isLoading={isLoading}
          type="submit"
          size={BUTTON_SIZES.LARGE}
        >
          {CREATE_CHANNEL_TEXT.BUTTON_LABEL}
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
